import React from "react";

import dayjs from "dayjs";
import { observer } from "mobx-react-lite";

import { IconFileFolderFill } from "@synthesis/shared-icons";
import { Checkbox, ContextMenu, type ContextMenuItem } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";

import { useStores } from "../../../store";

type WrapperProps = {
  children: React.ReactNode;
};

const Wrapper = React.memo<WrapperProps>(({ children, ...rest }) => {
  return (
    <section className="mb-16 flex flex-wrap gap-4" {...rest}>
      {children}
    </section>
  );
});

type FolderData = {
  id: string;
  baseName: string;
  modifiedAt: number;
};

type FolderProps = {
  data: FolderData;
  onClick: (data: FolderData) => void;
  onContextMenu: (data: FolderData) => void;
  contextMenuState: ContextMenuItem[];
};

const CheckboxWrapper = ({ isActiveCheck, className, ...props }: React.HTMLAttributes<HTMLDivElement> & { isActiveCheck?: boolean }) => (
  <div className={cn("absolute left-2 z-30 opacity-0 group-hover:opacity-100 sm:top-2", isActiveCheck && "opacity-100", className)} {...props} />
);

const Container = ({ isActiveCheck, className, ...props }: React.HTMLAttributes<HTMLDivElement> & { isActiveCheck?: boolean }) => (
  <div
    className={cn(
      "hover:bg-neutrals-99 group relative flex w-[10.25rem] cursor-pointer flex-col gap-1 rounded-[14px] pb-2 transition-colors",
      isActiveCheck && "bg-tertiary-95 hover:bg-tertiary-95/75",
      className,
    )}
    {...props}
  />
);

const Folder = observer<FolderProps>(({ onClick, contextMenuState, onContextMenu, data }) => {
  const { generalStore } = useStores();

  const onClickCheck = (folder: FolderData) => {
    generalStore.setCheckedContents({ id: folder.id, type: "DIRECTORY", size: 0 });
  };

  const isChecked = React.useMemo(() => generalStore.checkedFiles.get(data.id) !== undefined, [generalStore.checkedFiles, data]);

  return (
    <ContextMenu state={contextMenuState} onClick={() => onContextMenu(data)}>
      <Container data-cy="gridView-container-projectDocks" isActiveCheck={isChecked} onClick={() => onClick(data)}>
        <figure className="mx-auto">
          <IconFileFolderFill className="text-system-attention-50 h-[6.5rem] w-[6.5rem]" />
        </figure>
        <h2 data-cy="gridView-h2-projectDocks" className="text-type-common paragraph-sm mt-2 px-2">
          {data.baseName}
        </h2>
        <p className="text-type-light paragraph-sm mb-2 px-2">{dayjs.unix(data.modifiedAt).format("DD.MM.YYYY HH:mm:ss")}</p>

        <CheckboxWrapper isActiveCheck={isChecked} onClick={(e) => e.stopPropagation()}>
          <Checkbox onChange={() => onClickCheck(data)} checked={isChecked} />
        </CheckboxWrapper>
      </Container>
    </ContextMenu>
  );
});

const GridView = {
  Folder,
  Wrapper,
};

export default GridView;
